import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { GOVERNANCE_STAGES, governanceStageForPath } from './processRouteMap'
import { positionEvidence } from './GovernancePositionContent'

const RESPONSIBILITY_GROUPS = Object.freeze([
  { key: 'management-planning', label: 'Management Planning', stages: ['planning', 'proposal'] },
  { key: 'board-governance', label: 'Board Governance', stages: ['board-review', 'board-decision'] },
  { key: 'management-execution', label: 'Management Execution', stages: ['program', 'execution'] },
  { key: 'outcome-governance', label: 'Outcome Governance', stages: ['benefits'] },
])

const POSITION_CODES = ['INVESTMENT_PLANNING', 'INVESTMENT_PROPOSAL', 'BOARD_REVIEW', 'BOARD_DECISION', 'INVESTMENT_PROGRAM', 'PROGRAM_EXECUTION', 'BENEFITS_GOVERNANCE']

function StageContent({ stage, position, active, evidence }) {
  return <span className="flex min-w-0 items-start gap-2">
    <span className="w-5 shrink-0 pt-0.5 text-xs font-semibold tabular-nums text-slate-400" data-lifecycle-position>{String(position).padStart(2, '0')}</span>
    <span className="min-w-0 flex-1">
      <span className="block">{stage.label}</span>
      {active && <span className="mt-0.5 block text-[10px] font-bold uppercase tracking-wider">Current</span>}
      {evidence && <span className="mt-0.5 block text-xs font-normal leading-tight text-slate-500">{positionEvidence(evidence)}</span>}
    </span>
  </span>
}

export default function InvestmentGovernanceProcessNav({ currentStage, positions = [] }) {
  const { pathname } = useLocation()
  const current = currentStage === undefined ? governanceStageForPath(pathname) : currentStage

  return <nav aria-label="Investment governance process" className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Governance path</p>
    <div className="mt-3 grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-1">
      {RESPONSIBILITY_GROUPS.map((group, groupIndex) => <section aria-labelledby={`governance-group-${group.key}`} className={groupIndex ? 'border-t border-slate-200 pt-3 sm:border-t-0 sm:pt-0 lg:border-t lg:pt-3' : ''} data-governance-group={group.key} key={group.key}>
        <h2 className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500" id={`governance-group-${group.key}`}>{group.label}</h2>
        <ol className="mt-2 space-y-1">
          {group.stages.map((stageKey) => {
            const stage = GOVERNANCE_STAGES.find(({ key }) => key === stageKey)
            const position = GOVERNANCE_STAGES.indexOf(stage) + 1
            const active = current === stage.key
            const evidence = positions.find(({ code }) => code === POSITION_CODES[position - 1])
            const activeClass = active ? 'border-blue-300 bg-blue-50 font-bold text-blue-950' : ''
            const attention = evidence?.display_state === 'ATTENTION_REQUIRED'
            return <li data-governance-stage={stage.key} key={stage.key}>{stage.path
              ? <Link aria-current={active ? 'page' : undefined} aria-label={attention ? `${stage.label}, attention required` : undefined} className={`block rounded-lg border px-2.5 py-1.5 text-sm ${activeClass || (attention ? 'border-amber-300 bg-amber-50 text-slate-900' : 'border-transparent text-slate-700 hover:border-slate-300 hover:bg-slate-50')} focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600`} to={stage.path}><StageContent active={active} evidence={evidence} position={position} stage={stage} /></Link>
              : <div aria-current={active ? 'page' : undefined} className={`rounded-lg border px-2.5 py-1.5 text-sm ${activeClass || 'border-dashed border-slate-300 bg-white text-slate-700'}`}>
                <StageContent active={active} evidence={evidence} position={position} stage={stage} />
                <p className="ml-7 mt-0.5 text-xs font-normal leading-tight text-slate-500"><span className="mr-1 font-bold uppercase tracking-wider">Contextual</span> · <span>Available from a submitted Investment Proposal.</span></p>
              </div>}
            </li>
          })}
        </ol>
      </section>)}
    </div>
    <p className="mt-4 text-xs leading-relaxed text-slate-500">Lifecycle positions provide orientation, not completion status. Execution and benefits activity may overlap.</p>
  </nav>
}
